import { useState } from "react";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import zxcvbn from "zxcvbn";
import { useAppDispatch } from "../../redux/hooks/useTypedSelector";
import {
  resetAuth,
  resetPasswordAction,
} from "../../redux/slice/auth/authSlice";
import {
  ResetPassword as IResetPassword,
  ResetPasswordValidationSchema,
} from "./schema";
import { PageHero } from "../../components/public/PageHero";
import { Icon } from "../../components/public/ui";
import {
  FormAlert,
  PasswordField,
  SubmitButton,
} from "../../components/public/auth/fields";
import { authErrorMessage } from "../../components/public/auth/authErrorMessage";
import { useAuthModal } from "../../components/public/auth/AuthModalContext";

type CriteriaType = "length" | "uppercase" | "lowercase" | "special_character";

const criteria: {
  type: CriteriaType;
  label: string;
  test: (p: string) => boolean;
}[] = [
  { type: "length", label: "8 or more characters", test: (p) => p?.length > 7 },
  {
    type: "uppercase",
    label: "One uppercase letter",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    type: "lowercase",
    label: "One lowercase letter",
    test: (p) => /[a-z]/.test(p),
  },
  {
    type: "special_character",
    label: "One special character",
    test: (p) => /[\W_]/.test(p),
  },
];

const strengthLabel = (password: string) =>
  password
    ? ["Weak", "Medium", "Medium", "Strong", "Strong"][zxcvbn(password).score]
    : "";

const strengthStyle: Record<
  string,
  { bar: string; text: string; width: string }
> = {
  Weak: { bar: "bg-error", text: "text-error", width: "w-1/3" },
  Medium: { bar: "bg-accent-warm", text: "text-accent-warm", width: "w-2/3" },
  Strong: { bar: "bg-tertiary", text: "text-tertiary", width: "w-full" },
};

export const ResetPassword = () => {
  const dispatch = useAppDispatch();
  const { openAuth } = useAuthModal();
  const { email, token } = useParams();
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  let decodedEmail: { email?: string } | undefined;
  try {
    decodedEmail = JSON.parse(atob(email ?? ""));
  } catch {}

  const {
    handleSubmit,
    register,
    watch,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(ResetPasswordValidationSchema),
    defaultValues: {
      password: "",
      email: decodedEmail?.email ?? "null",
      token: token ?? "null",
    },
  });

  const password = watch("password");
  const strength = strengthLabel(password);
  const isValid = criteria.every((c) => c.test(password));

  const onSubmit = async (data: IResetPassword) => {
    setError("");
    const res = await dispatch(resetPasswordAction(data));
    if (resetPasswordAction.fulfilled.match(res)) {
      setDone(true);
    } else {
      setError(
        authErrorMessage(
          res.payload,
          "We couldn't reset your password. The link may have expired — please request a new one.",
        ),
      );
    }
    dispatch(resetAuth());
  };

  return (
    <main>
      <PageHero
        eyebrow="Account"
        title="Set a New Password"
        description={
          decodedEmail?.email ? (
            <>
              For{" "}
              <strong className="text-on-surface">{decodedEmail.email}</strong>
            </>
          ) : undefined
        }
      />
      <section className="w-full pb-20 bg-surface-container-low/60">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="bg-surface-container-lowest rounded-3xl shadow-xl p-6 sm:p-8">
            {done ? (
              <div className="flex flex-col items-center text-center gap-5 py-2">
                <div className="w-14 h-14 rounded-2xl bg-tertiary-fixed text-tertiary flex items-center justify-center">
                  <Icon name="lock_reset" className="text-3xl" />
                </div>
                <div className="flex flex-col gap-2">
                  <h2 className="font-headline-md text-headline-md text-on-surface">
                    Password updated
                  </h2>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    You can now sign in with your new password.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openAuth("signin")}
                  className="w-full h-12 rounded-xl bg-primary hover:bg-primary-container text-on-primary font-label-lg text-label-lg shadow-lg shadow-primary/25 transition-colors"
                >
                  Sign In
                </button>
              </div>
            ) : (
              <form
                className="flex flex-col gap-5"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                {error && <FormAlert tone="error">{error}</FormAlert>}

                <PasswordField
                  id="reset-password"
                  label="New password"
                  autoComplete="new-password"
                  placeholder="Enter a new password"
                  error={errors.password?.message}
                  {...register("password")}
                />

                {password && (
                  <div className="flex flex-col gap-1.5">
                    <div className="h-1.5 w-full rounded-full bg-surface-container-highest overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${strengthStyle[strength]?.bar} ${strengthStyle[strength]?.width}`}
                      />
                    </div>
                    <span className="font-body-sm text-xs text-on-surface-variant">
                      Strength:{" "}
                      <strong className={strengthStyle[strength]?.text}>
                        {strength}
                      </strong>
                    </span>
                  </div>
                )}

                <ul className="list-none m-0 p-0 grid grid-cols-1 xs:grid-cols-2 gap-2">
                  {criteria.map((c) => {
                    const met = c.test(password);
                    return (
                      <li
                        key={c.type}
                        className={`flex items-center gap-2 font-body-sm text-xs ${met ? "text-tertiary" : "text-on-surface-variant"}`}
                      >
                        <Icon
                          name={met ? "check_circle" : "radio_button_unchecked"}
                          className="text-base"
                        />
                        {c.label}
                      </li>
                    );
                  })}
                </ul>

                {isValid ? (
                  <SubmitButton loading={isSubmitting}>
                    Reset Password
                  </SubmitButton>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="w-full h-12 rounded-xl bg-surface-container text-on-surface-variant font-label-lg text-label-lg cursor-not-allowed"
                  >
                    Reset Password
                  </button>
                )}

                <p className="font-body-sm text-body-sm text-on-surface-variant text-center">
                  Remembered your password?{" "}
                  <button
                    type="button"
                    onClick={() => openAuth("signin")}
                    className="font-label-md text-label-md text-primary hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};
